/**
 * UUID generator import for creating unique alert IDs
 */
import { v4 as uuidv4 } from 'uuid';

/**
 * Valid alert types that can be used
 */
export type AlertType = 'success' | 'error' | 'warning' | 'info';

/**
 * Interface defining the structure of an alert item
 */
export type AlertItem = {
  id: string;
  type: AlertType;
  message: string;
  title?: string;
  tags: string[];
  delay?: number; // Optional delay before alert is shown
  onClose: () => void;
}

/**
 * Type definition for alert listener functions
 */
type Listener = (alerts: AlertItem[]) => void;

/**
 * Class that manages layout alerts including creation, notification and cleanup
 */
class LayoutAlertManager {
  private alerts: AlertItem[] = [];
  private listeners: Listener[] = [];

  /**
   * Notifies all registered listeners with current alerts
   */
  private notify() {
    this.listeners.forEach((listener) => listener(this.alerts));
  }

  /**
   * Subscribes a listener to alert changes
   * @param listener Function to be called when alerts change
   * @returns Unsubscribe function
   */
  subscribe(listener: Listener) {
    console.log('subscribe', this.alerts);
    this.listeners.push(listener);
    listener(this.alerts);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Shows a new layout alert
   * @param params Object containing alert properties
   * @returns Generated alert ID
   */
  showLayoutAlert({ type, message, title = '', tags = [], onClose = () => null, delay = 15 }: Omit<AlertItem, 'id'>): string {
    const id = uuidv4();
    this.alerts = [...this.alerts, { id, type, message, title, tags, onClose, delay }];
    this.notify();
    if (delay && delay > 0) {
      setTimeout(() => {
        this.closeLayoutAlert(id);
        onClose();
      }, delay * 1000);
    }
    return id;
  }

  /**
   * Closes an alert by ID
   * @param id ID of alert to close
   */
  closeLayoutAlert(id: string) {
    this.alerts = this.alerts.filter((alert) => alert.id !== id);
    this.notify();
  }

  /**
   * Alias for closeLayoutAlert
   * @param id ID of alert to close
   */
  closeLayoutAlertOf(id: string) {
    this.closeLayoutAlert(id);
  }

  /**
   * Closes all alerts with specified tag
   * @param tag Tag to filter alerts by
   */
  closeLayoutAlertsByTag(tag: string) {
    this.alerts = this.alerts.filter((alert) => !alert?.tags.includes(tag));
    this.notify();
  }

  /**
   * Closes all alerts that contain any of the specified tags
   * @param tags Array of tags to filter alerts by
   */
  closeLayoutAlertsByTags(tags: string[]) {
    this.alerts = this.alerts.filter((alert) => !tags.some((tag) => alert.tags.includes(tag)));
    this.notify();
  }

  /**
   * Gets current alerts
   * @returns Array of current alerts
   */
  getAlerts() {
    return this.alerts;
  }
}

/**
 * Singleton instance of LayoutAlertManager
 */
export const layoutAlertManager = new LayoutAlertManager();

/**
 * Bound method to show a layout alert
 */
export const showLayoutAlert = layoutAlertManager.showLayoutAlert.bind(layoutAlertManager);

/**
 * Shows a success alert
 * @param msg Alert message
 * @param title Optional alert title
 */
export const showLayoutAlertSuccess = (msg: string, title?: string, onClose: () => void = () => null, tags = []) =>
  layoutAlertManager.showLayoutAlert({ type: 'success', message: msg, title, onClose, tags });

/**
 * Shows a warning alert
 * @param msg Alert message
 * @param title Optional alert title
 */
export const showLayoutAlertWarning = (msg: string, title?: string, onClose: () => void = () => null, tags = []) =>
  layoutAlertManager.showLayoutAlert({ type: 'warning', message: msg, title, onClose, tags });

/**
 * Shows an error alert
 * @param msg Alert message
 * @param title Optional alert title
 */
export const showLayoutAlertError = (msg: string, title?: string, onClose: () => void = () => null, tags = []) =>
  layoutAlertManager.showLayoutAlert({ type: 'error', message: msg, title, onClose, tags });

/**
 * Shows an info alert
 * @param msg Alert message
 * @param title Optional alert title
 */
export const showLayoutAlertInfo = (msg: string, title?: string, onClose: () => void = () => null, tags = []) =>
  layoutAlertManager.showLayoutAlert({ type: 'info', message: msg, title, onClose, tags });

/**
 * Bound method to close a layout alert by ID
 */
export const closeLayoutAlert = layoutAlertManager.closeLayoutAlert.bind(layoutAlertManager);

/**
 * Bound method to close a layout alert by ID (alias)
 */
export const closeLayoutAlertOf = layoutAlertManager.closeLayoutAlertOf.bind(layoutAlertManager);

/**
 * Bound method to close layout alerts by tag
 */
export const closeLayoutAlertsByTag = layoutAlertManager.closeLayoutAlertsByTag.bind(layoutAlertManager);

/**
 * Bound method to close layout alerts by multiple tags
 */
export const closeLayoutAlertsByTags = layoutAlertManager.closeLayoutAlertsByTags.bind(layoutAlertManager);
